import { useEffect, useState } from 'react';
import API from 'api/API';
import { generateFilePath } from 'pages/Device/DevicePage.functions';
import { getFile, getFileMetadata } from 'services/FileService';

interface UseFileMetadata {
  src: string;
  fileData?: fileMetadataModel;
  loading: boolean;
}

export interface fileMetadataModel {
  name: string;
  lastModified: Date;
}

const useFileMetadata = (deviceId: string, firebase?: boolean, file?: File, fileUrl?: string): UseFileMetadata => {
  const [loading, setLoading] = useState(true);
  const [fileData, setFileData] = useState<fileMetadataModel>();
  const [src, setSrc] = useState<string>('');

  useEffect(() => {
    setLoading(true);
    (async (): Promise<void> => {
      try {
        // If file from dropzone is uploaded, check from file first
        if (file) {
          // Set the source file path/resolvedSrc
          const docUrl = generateFilePath(deviceId, file.name);
          // Use firebase to grab the URL for download
          const resolvedSrc = firebase ? await getFile(docUrl, false, true) : await API.asset.get(docUrl);
          // Convert the last modified string from object to a date object
          const uploadDate = new Date(file.lastModified);

          // Set states
          setFileData({ name: file.name, lastModified: uploadDate });
          setSrc(resolvedSrc);
        }
        // If no file found, grab the data from the stored fileUrl
        else if (fileUrl) {
          // Use firebase to grab the URL for download
          const resolvedSrc = firebase ? await getFile(fileUrl, false, false) : await API.asset.get(fileUrl);
          // Use getFileMetadata helper to grab the meta data object
          const fileMetadata = firebase ? await getFileMetadata(resolvedSrc) : null;
          // Convert the updated string from object to a date object
          const uploadDate = new Date(fileMetadata?.updated ?? '');

          // Set states
          if (fileMetadata) {
            setFileData({ name: fileMetadata.name, lastModified: uploadDate });
            setSrc(resolvedSrc);
          } else {
            throw new Error('No meta data has been found');
          }
        }
      } catch (error) {
        API.errorHandler(error);
      } finally {
        setLoading(false);
      }
    })();
  }, [deviceId, file, fileUrl, firebase, src]);

  return { loading, fileData, src };
};

export default useFileMetadata;
